import { DateValue } from '@internationalized/date';
import {
  Controller,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import {
  dobInvalidMessage,
  getUnderRequiredAgeDob,
  invalidDateOfBirth,
  ssnRequiredMessage,
  validateDob,
  validateSsn,
} from '@consumer-frontend/nc-utils';
import { Fieldset } from '@consumer-frontend/shared-ui';

import { NcButton } from '../NcButton';
import { NcDatePicker } from '../NcDatePicker';
import { NcForm } from '../NcForm';
import { NcSsnInput } from '../NcSsnInput';

export type VerifyIdentityFormValuesT = {
  ssn: string;
  dateOfBirth?: DateValue;
};

export type NcVerifyIdentityFormPropsT = {
  onSubmit: SubmitHandler<VerifyIdentityFormValuesT>;
  isLoading?: boolean;
  onBack?: () => Promise<void | Error>;
};

export type NcVerifyIdentityFormT = React.FC<NcVerifyIdentityFormPropsT>;

export const NcVerifyIdentityForm: NcVerifyIdentityFormT = ({
  isLoading = false,
  onBack,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyIdentityFormValuesT>({
    defaultValues: {
      dateOfBirth: undefined,
      ssn: '',
    },
    reValidateMode: 'onBlur',
  });

  const maximumDob = getUnderRequiredAgeDob();

  return (
    <NcForm
      className="d-flex flex-column gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="ssn"
        rules={{
          required: ssnRequiredMessage,
          validate: validateSsn,
        }}
        render={({ field }) => (
          <NcSsnInput
            {...field}
            errorMessage={errors.ssn?.message}
            id="ssn"
            label="Social Security Number"
            name="ssn"
          />
        )}
      />

      <Controller
        control={control}
        name="dateOfBirth"
        rules={{
          required: dobInvalidMessage,
          validate: validateDob,
        }}
        render={({ field }) => (
          <NcDatePicker
            defaultValue={field.value}
            errorMessage={errors.dateOfBirth?.message}
            hideCalendar
            id="dateOfBirth"
            label="Date of Birth"
            max={maximumDob}
            min={invalidDateOfBirth}
            onBlur={() => field.onBlur()}
            onChange={field.onChange}
          />
        )}
      />
      <Fieldset className="cta-buttons">
        <NcButton loading={isLoading || isSubmitting} type="submit">Continue</NcButton>

        {
          onBack && (
            <NcButton
              disabled={isLoading || isSubmitting}
              onClick={onBack}
              type="button"
              variant="outline-primary"
            >
              Back
            </NcButton>
          )
        }
      </Fieldset>
    </NcForm>
  );
};
